import { useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, FileCode2, Folder } from 'lucide-react';
import type { GitDiffResult } from '@shared/agent-protocol';
import { PatchDiff } from '@/components/PatchDiff';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import type { ApiResponse } from '@/lib/api-contract';
import { cn } from '@/lib/utils';
import { fileKindLabel, fileTree, type ChangedFiles, type FileTreeNode } from './agent-activity';

/** Reads one file's diff: a run's, from its worktree on the machine (GET /api/runs/:id/diff). */
export type DiffReader = (path: string, opts: { signal?: AbortSignal }) => Promise<ApiResponse<{ diff: GitDiffResult }>>;

/** +12 −3, the minus a real one. Null counts show as nothing. */
export function LineCounts({ additions, deletions, className }: { additions: number | null; deletions: number | null; className?: string }) {
  return (
    <span className={cn('shrink-0 font-mono tabular-nums', className)}>
      {additions != null ? <span className="text-success-strong">+{additions}</span> : null}
      {deletions != null ? <span className="ml-1 text-danger-strong">−{deletions}</span> : null}
    </span>
  );
}

/**
 * SF-T-222: the lines an agent's thread changed, as a small +/− button; it opens a popup with the changed files as a
 * tree, each with its own counts. With `readDiff`, picking a file widens the popup to show its diff beside the tree.
 */
export function ChangedFilesButton({ changes, readDiff, className }: { changes: ChangedFiles; readDiff?: DiffReader; className?: string }) {
  const [selected, setSelected] = useState<string | null>(null);
  if (!changes.files.length) return null;
  const count = changes.files.length;
  return (
    <Popover onOpenChange={(open) => (open ? undefined : setSelected(null))}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn('inline-flex items-center gap-1.5 rounded-full bg-muted/70 px-2 py-0.5 text-2xs hover:bg-muted', className)}
          aria-label={`${count} changed ${count === 1 ? 'file' : 'files'}: ${changes.additions} lines added, ${changes.deletions} removed`}
        >
          <LineCounts additions={changes.additions} deletions={changes.deletions} />
          <span className="text-muted-foreground">
            {count} {count === 1 ? 'file' : 'files'}
          </span>
          <ChevronDown className="size-3 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        className={cn('flex max-h-[min(70vh,40rem)] gap-0 p-0 transition-[width]', selected ? 'w-[min(60rem,calc(100vw-1rem))]' : 'w-80')}
      >
        <div className={cn('min-h-0 shrink-0 overflow-y-auto py-1.5', selected ? 'w-64 border-r' : 'w-full')}>
          <FileTree files={changes.files} selected={selected} onPick={readDiff ? setSelected : undefined} />
          {readDiff ? null : <p className="px-3 pt-1.5 text-3xs text-muted-foreground">Diffs show for a run's worktree.</p>}
        </div>
        {selected && readDiff ? <FileDiff key={selected} path={selected} readDiff={readDiff} /> : null}
      </PopoverContent>
    </Popover>
  );
}

/** One row of the tree as the keyboard walks it: folders and files in order, with their level and place among siblings. */
interface TreeRow {
  node: FileTreeNode;
  /** 1 at the top. */
  level: number;
  pos: number;
  size: number;
  key: string;
}

function treeRows(nodes: FileTreeNode[], level = 1, parent = ''): TreeRow[] {
  return nodes.flatMap((node, i) => {
    const key = `${parent}/${node.name}`;
    const row: TreeRow = { node, level, pos: i + 1, size: nodes.length, key };
    return node.file ? [row] : [row, ...treeRows(node.children, level + 1, key)];
  });
}

/**
 * The changed files as a tree (`ui-components.project.mdc`, "Keyboard and screen readers"): one Tab stop, the arrow
 * keys, Home and End move between rows, Enter or Space opens a file's diff. Folders are always open.
 */
function FileTree({ files, selected, onPick }: { files: ChangedFiles['files']; selected: string | null; onPick?: (path: string) => void }) {
  const rows = treeRows(fileTree(files));
  const [focused, setFocused] = useState(0);
  const current = Math.min(focused, rows.length - 1);
  const list = useRef<HTMLUListElement>(null);
  const moveTo = (index: number) => {
    const next = Math.max(0, Math.min(rows.length - 1, index));
    setFocused(next);
    list.current?.querySelector<HTMLElement>(`[data-row="${next}"]`)?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => moveTo(current + 1),
      ArrowUp: () => moveTo(current - 1),
      Home: () => moveTo(0),
      End: () => moveTo(rows.length - 1),
      Enter: () => rows[current]?.node.file && onPick?.(rows[current]!.node.file!.path),
      ' ': () => rows[current]?.node.file && onPick?.(rows[current]!.node.file!.path),
    };
    const act = keys[event.key];
    if (!act) return;
    event.preventDefault();
    act();
  };
  return (
    <ul ref={list} role="tree" aria-label="Changed files" className="text-2xs" onKeyDown={onKeyDown}>
      {rows.map((row, index) => {
        const f = row.node.file;
        const pad = { paddingLeft: `${0.75 + (row.level - 1) * 0.75}rem` };
        const kind = f ? fileKindLabel(f.kind) : null;
        const isSelected = !!f && selected === f.path;
        return (
          <li
            key={row.key}
            role="treeitem"
            data-row={index}
            tabIndex={index === current ? 0 : -1}
            aria-level={row.level}
            aria-posinset={row.pos}
            aria-setsize={row.size}
            aria-expanded={f ? undefined : true}
            aria-selected={f ? isSelected : undefined}
            aria-label={f ? `${f.path}${kind ? `, ${kind}` : ''}: ${f.additions ?? '?'} added, ${f.deletions ?? '?'} removed` : row.node.name}
            className={cn(
              'flex items-center gap-1 py-0.5 pr-3 outline-none focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              f ? onPick && 'cursor-pointer hover:bg-muted' : 'text-muted-foreground',
              isSelected && 'bg-muted font-medium'
            )}
            style={pad}
            onFocus={() => setFocused(index)}
            onClick={f && onPick ? () => onPick(f.path) : undefined}
          >
            {f ? <FileCode2 className="size-3 shrink-0 text-muted-foreground" aria-hidden /> : <Folder className="size-3 shrink-0" aria-hidden />}
            <span className="min-w-0 truncate font-mono" title={f?.path}>
              {row.node.name}
            </span>
            {kind ? <span className={cn('shrink-0', f!.kind === 'failed' ? 'text-danger-strong' : 'text-muted-foreground')}>{kind}</span> : null}
            {f ? <LineCounts additions={f.additions} deletions={f.deletions} className="ml-auto pl-2" /> : null}
          </li>
        );
      })}
    </ul>
  );
}

function FileDiff({ path, readDiff }: { path: string; readDiff: DiffReader }) {
  const loader = useLoader(
    async (signal) => {
      const res = await readDiff(path, { signal });
      return res.data ? { diff: res.data.diff, error: null } : { diff: null, error: describeApiError(res) };
    },
    [path, readDiff]
  );
  const got = loader.data;
  return (
    <div className="min-h-0 min-w-0 flex-1 overflow-auto p-2">
      {!got ? (
        <LoadingState inline label="Reading the diff from the machine…" className="px-1 py-2" />
      ) : got.error ? (
        <LoadError compact title="Couldn't read the diff" error={got.error} onRetry={loader.reload} />
      ) : got.diff && got.diff.diff.trim() ? (
        <>
          <PatchDiff diff={got.diff.diff} />
          {got.diff.truncated ? <p className="pt-1.5 text-3xs text-muted-foreground">The diff is long; this is its start.</p> : null}
        </>
      ) : (
        <p className="px-1 py-2 text-xs text-muted-foreground">No changes against the branch's base now: the run may have undone them.</p>
      )}
    </div>
  );
}
